import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { APIData, PopupData, PopupListItem, Service } from 'webapp-types';
import { isAlive } from 'webapp/utils/common';

import type { PreviewRow } from 'webapp/components/custom-query/setup/common/preview-table';
import TableSorterComponent from 'webapp/components/table-sorter';

import type FilterSortQueryPreview from 'webapp/models-custom/filter-sort/query-preview';

type Action = 'openAttributesMap' | 'openAttributesSkip';

interface Args {
	model: APIData.CustomQuery.PreviewHeader;
	filterSort: FilterSortQueryPreview;
	allowTransformations: boolean;
	openAttributeTransform: (
		data: Omit<PopupData.CustomQuery.TransformAttributes, 'possibleAttributes' | 'form'>,
	) => void;
	dimensionName?: string; // only used for the "isAttribute" header
}

export default class CustomQuerySetupCommonPreviewTableHeaderComponent extends Component<Args> {
	@service('popup') declare popupService: Service.Popup;

	get sortPropertyName(): keyof PreviewRow | '' {
		if (this.args.model.isDate) {
			return 'dateValue';
		}
		if (this.args.model.isAttribute) {
			return 'attributeValue';
		}
		if (this.args.model.isMetric) {
			return 'metricValue';
		}
		return '';
	}

	get headerName(): string {
		if (this.args.model.isDate) {
			return 'Date';
		}
		if (this.args.model.isAttribute) {
			const baseText = 'Dimension value';
			const attributeName = this.args.dimensionName;
			if (attributeName) {
				return `${baseText} (${attributeName})`;
			}
			return baseText;
		}
		if (this.args.model.isMetric) {
			return 'Value';
		}
		return '';
	}

	get dropdownOptions(): PopupListItem<Action>[] {
		const options: { text: string; action: Action }[] = [];

		if (this.showCustomizeOptions) {
			options.push({
				text: 'Rename dimension values',
				action: 'openAttributesMap',
			});
			options.push({
				text: 'Exclude dimension values',
				action: 'openAttributesSkip',
			});
		}
		return options;
	}

	get showCustomizeOptions(): boolean {
		return this.args.allowTransformations && !!this.args.model.isAttribute;
	}

	@action
	openCustomizeOptions(e: PointerEvent): void {
		const attachTo = e.target as HTMLElement;

		if (!attachTo) {
			return;
		}

		if (!this.showCustomizeOptions) {
			return;
		}

		const popup = this.popupService.createListPopup(attachTo, undefined, 'transform-options');

		if (popup) {
			popup.open({
				itemsList: this.dropdownOptions,
				doAction: (actionName: string) => {
					if (isAlive(this)) {
						this[actionName as Action]();
					}
				},
			});
		}
	}

	@action
	openAttributesMap(): void {
		this.args.openAttributeTransform({
			dataProperty: 'attributesMap',
		});
	}

	@action
	openAttributesSkip(): void {
		this.args.openAttributeTransform({
			dataProperty: 'attributesSkip',
		});
	}
	<template>
		<th>
			<TableSorterComponent
				@selectedProp={{@filterSort.sortProp}}
				@selectedOrder={{@filterSort.sortOrder}}
				@prop={{this.sortPropertyName}}
				@name={{this.headerName}}
				@changeProp={{@filterSort.changeSortProp}}
				@changeOrder={{@filterSort.changeSortOrder}}
			/>

			{{#if this.showCustomizeOptions}}
				<a class="action--customize" {{on "click" this.openCustomizeOptions}}>Customize</a>
			{{/if}}

		</th>
	</template>
}
